import { ArrowLeftIcon, ChatBubbleLeftRightIcon, CloudArrowUpIcon, FunnelIcon, MagnifyingGlassIcon, PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import { Callout } from '@/components/core/Callout';
import { Checkbox } from '@/components/core/Checkbox';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { StepProgress } from '@/components/core/StepProgress';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { Stepper, useStepper } from '@/hooks/useStepper';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { ChannelCard, OrganizationCard } from './components/RecipientCards';
import { SchedulePlan } from './components/SchedulePlan';
import { useChannelPublicationItems } from './hooks/useChannelPublicationItems';
import { usePublicationDestinations } from './hooks/usePublicationDestinations';
import { usePublishItems } from './hooks/usePublishItems';
import { useSavePublicationJob } from './hooks/useSavePublicationJob';
import {
    applyDefaultPreselection,
    applyJobPreselection,
} from './lib/publicationDestinations';
import {
    buildJobPayload,
    buildPublicationValuesPayload,
    getPublicationAttributes,
    getSelectedPublicationDestinations,
    hasAnySelectedDestination,
    resolveItemsQuery,
    restorePlanOptionsFromJob,
} from './lib/publicationValues';
import {
    filterChannelsByText,
    filterOrganizationsByText,
    selectAllRecipients,
    selectionUpdated,
    setDestinationSelection,
} from './lib/recipientSelection';
import type {
    ChannelDestination,
    CommunicationPlans,
    CustomPublicationSettings,
    ItemsByChannel,
    ItemSelection,
    OrganizationDestination,
    PlanOptions,
    PublicationData,
    PublicationMode,
} from './types';
import { PUBLICATION_MODE } from './types';

type PublishToRecipientsDialogProps = {
    itemSelection: ItemSelection;
    mode: PublicationMode;
    publicationData?: PublicationData;
    onClose: (completed?: boolean) => void;
};

type PublishToRecipientsDialogViewProps = PublishToRecipientsDialogProps & {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
    plans: CommunicationPlans;
    customPublicationSettings?: CustomPublicationSettings | null;
    user?: unknown;
    organization?: unknown;
    isDestinationsLoading: boolean;
    itemsByChannel: ItemsByChannel;
    isItemsLoading: boolean;
    onPublish: ReturnType<typeof usePublishItems>['mutate'];
    onSaveJob: ReturnType<typeof useSavePublicationJob>['mutate'];
    isSubmitting: boolean;
};

const itemsQueryRows = (itemsQuery: unknown): Array<{ label: string; value: string }> => {
    if (!itemsQuery) {
        return [];
    }
    if (Array.isArray(itemsQuery)) {
        return itemsQuery
            .filter((value) => Boolean(value))
            .map((value, index) => ({
                label: String(index + 1),
                value: String(value),
            }));
    }
    return Object.entries(itemsQuery as Record<string, unknown>)
        .filter(([, value]) => Boolean(value))
        .map(([key, value]) => ({
            label: `${key}1`,
            value: String(value),
        }));
};

const PublishToRecipientsDialogView = ({
    itemSelection,
    mode,
    publicationData,
    onClose,
    organizations: loadedOrganizations,
    communicationChannels: loadedChannels,
    plans,
    customPublicationSettings,
    user,
    organization,
    isDestinationsLoading,
    itemsByChannel,
    isItemsLoading,
    onPublish,
    onSaveJob,
    isSubmitting,
}: PublishToRecipientsDialogViewProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { currentStep, nextStep, prevStep, steps } = useStepper();
    const [open, setOpen] = useState(true);
    const [channelFilter, setChannelFilter] = useState('');
    const [allRecipientsSelected, setAllRecipientsSelected] = useState(false);
    const [organizations, setOrganizations] = useState<OrganizationDestination[]>([]);
    const [communicationChannels, setCommunicationChannels] = useState<ChannelDestination[]>([]);
    const [planOptions, setPlanOptions] = useState<PlanOptions>({
        action: 'IMMEDIATELY',
        job: { schedule: {} },
    });
    const didInitialize = useRef(false);
    const form = useForm<ItemsByChannel>({ values: itemsByChannel });

    const itemsQuery = useMemo(() => resolveItemsQuery(itemSelection), [itemSelection]);
    const isLoading = isDestinationsLoading || isItemsLoading;
    const isEmpty = !isLoading && organizations.length === 0 && communicationChannels.length === 0;

    useEffect(() => {
        if (isDestinationsLoading || didInitialize.current) {
            return;
        }
        didInitialize.current = true;
        let nextOrganizations = loadedOrganizations;
        let nextChannels = loadedChannels;
        if (publicationData?.job) {
            const restored = applyJobPreselection(
                loadedOrganizations,
                loadedChannels,
                publicationData.job,
            );
            nextOrganizations = restored.organizations;
            nextChannels = restored.communicationChannels;
            setPlanOptions(restorePlanOptionsFromJob(publicationData.job));
        } else {
            const preselected = applyDefaultPreselection(loadedOrganizations, loadedChannels);
            nextOrganizations = preselected.organizations;
            nextChannels = preselected.communicationChannels;
        }
        setOrganizations(nextOrganizations);
        setCommunicationChannels(nextChannels);

        // Angular only skipped the picker in onWizardInit, not after a later card click.
        if (!customPublicationSettings?.selectPublicationTargets) {
            return;
        }
        const selected = getSelectedPublicationDestinations(nextOrganizations, nextChannels);
        const targets = customPublicationSettings.filterPublicationTargets
            ? customPublicationSettings.filterPublicationTargets(selected, mode, user, organization)
            : selected;
        const selectedTargets = customPublicationSettings.selectPublicationTargets(
            itemSelection.primaryKeys,
            targets,
            user,
            organization,
        );
        if (selectedTargets !== null) {
            nextStep();
        }
    }, [
        customPublicationSettings,
        isDestinationsLoading,
        itemSelection.primaryKeys,
        loadedChannels,
        loadedOrganizations,
        mode,
        nextStep,
        organization,
        publicationData,
        user,
    ]);

    const filteredOrganizations = useMemo(
        () => filterOrganizationsByText(organizations, channelFilter),
        [channelFilter, organizations],
    );
    const filteredChannels = useMemo(
        () => filterChannelsByText(communicationChannels, channelFilter),
        [channelFilter, communicationChannels],
    );

    useEffect(() => {
        const next = selectionUpdated({
            organizations,
            communicationChannels,
            filteredOrganizations,
            filteredChannels,
            previousAllRecipientsSelected: allRecipientsSelected,
            searchTextChanged: true,
        });
        setAllRecipientsSelected(next.allRecipientsSelected);
        // Only recompute the checkbox after a filter change; do not wipe hidden selections.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelFilter, filteredOrganizations, filteredChannels]);

    const closeDialog = (completed = false) => {
        setOpen(false);
        onClose(completed);
    };

    const goTo = (path: string) => {
        closeDialog();
        window.location.assign(path);
    };

    const applySelectionChange = (
        nextOrganizations: OrganizationDestination[],
        nextChannels: ChannelDestination[],
        searchTextChanged = false,
    ) => {
        const next = selectionUpdated({
            organizations: nextOrganizations,
            communicationChannels: nextChannels,
            filteredOrganizations: filterOrganizationsByText(nextOrganizations, channelFilter),
            filteredChannels: filterChannelsByText(nextChannels, channelFilter),
            previousAllRecipientsSelected: allRecipientsSelected,
            searchTextChanged,
        });
        setOrganizations(next.organizations);
        setCommunicationChannels(next.communicationChannels);
        setAllRecipientsSelected(next.allRecipientsSelected);
    };

    const handleSelectAll = (isSelected: boolean) => {
        const next = selectAllRecipients(
            organizations,
            communicationChannels,
            filteredOrganizations,
            filteredChannels,
            isSelected,
        );
        applySelectionChange(next.organizations, next.communicationChannels, true);
    };

    const confirmPublication = () => {
        const destinations = getSelectedPublicationDestinations(organizations, communicationChannels);
        if (destinations.length < 1) {
            toast.warning('NO_ORGANIZATIONS_SELECTED');
            return;
        }

        const formItems = form.getValues() as ItemsByChannel;
        const mergedItems = { ...itemsByChannel, ...formItems };
        const channelDestinations = destinations.filter(
            (destination) => destination.destinationType === 'COMMUNICATION_CHANNEL',
        );
        const valuesPayload = buildPublicationValuesPayload(
            channelDestinations,
            communicationChannels,
            mergedItems,
            plans,
            mode,
        );

        if (planOptions.action === 'IMMEDIATELY') {
            onPublish({
                mode,
                itemSelection,
                destinations,
                valuesPayload,
            }, {
                onSuccess: () => closeDialog(true),
            });
            return;
        }

        const job = buildJobPayload({
            selectionId: publicationData?.selectionId ?? '',
            mode,
            organizations,
            communicationChannels,
            itemsByChannel: mergedItems,
            plans,
            planOptions,
        });
        onSaveJob({
            itemSelection,
            publicationData,
            job,
        }, {
            onSuccess: () => closeDialog(true),
        });
    };

    const handleNext = () => {
        if (!hasAnySelectedDestination(organizations, communicationChannels)) {
            toast.warning('NO_ORGANIZATIONS_SELECTED');
            return;
        }
        if (customPublicationSettings?.enablePublicationSchedule) {
            const destinations = getSelectedPublicationDestinations(organizations, communicationChannels);
            const isScheduleEnabled = customPublicationSettings.enablePublicationSchedule(
                itemSelection.primaryKeys,
                destinations,
                user,
                organization,
            );
            if (!isScheduleEnabled) {
                confirmPublication();
                return;
            }
        }
        nextStep();
    };

    const title = mode === PUBLICATION_MODE.DEPUBLISH
        ? t('ITEM_LIBRARY_DEPUBLISH')
        : t('PUBLISH_TO_ORGANIZATION');
    const stepNotification = currentStep === 1
        ? (mode === PUBLICATION_MODE.DEPUBLISH
            ? t('ITEM_LIBRARY_DEPUBLISH.CHANNEL.DESCRIPTION')
            : t('ITEM_LIBRARY_PUBLISH.CHANNEL.DESCRIPTION'))
        : t('ITEM_LIBRARY_PUBLISH.SCHEDULE.DESCRIPTION');
    const lastStepLabel = mode === PUBLICATION_MODE.DEPUBLISH
        ? t('ITEM_LIBRARY_DEPUBLISH_BUTTON')
        : t('ITEM_LIBRARY_PUBLISH_BUTTON');
    const noMatches = filteredOrganizations.length < 1 && filteredChannels.length < 1;
    const queryRows = itemsQueryRows(itemsQuery);

    return (
        <FormProvider {...form}>
            <Dialog
                open={open}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                        closeDialog();
                    }
                }}
            >
                <DialogContent
                    variant="large"
                    data-testid="publish-to-recipients-dialog"
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogHeaderDefault>{title}</DialogHeaderDefault>
                    </DialogHeader>
                    {isLoading ? (
                        <DialogBody padding="none" className="tw-h-[80vh] tw-flex tw-flex-1 tw-items-center tw-justify-center">
                            <Loader />
                        </DialogBody>
                    ) : isEmpty ? (
                        <DialogBody
                            padding="none"
                            className="tw-flex tw-flex-col tw-items-stretch tw-gap-20 tw-px-5 tw-pt-10 tw-pb-16 lg:tw-px-8 lg:tw-pt-16 lg:tw-pb-20"
                            data-testid="publish-empty-state"
                        >
                            <p className="tw-m-0 tw-text-center tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                {t('NODATA.PUBLISH_LEAD')}
                            </p>
                            <div className="tw-flex tw-flex-col tw-gap-10 md:tw-grid md:tw-grid-cols-2 md:tw-gap-5">
                                <div className="tw-flex tw-flex-col tw-items-center tw-gap-5">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                        <Icon
                                            icon={UserGroupIcon}
                                            className="tw-h-12 tw-w-12 tw-aspect-square tw-icon-primary"
                                        />
                                        <p className="tw-m-0 tw-text-center tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                            {t('NODATA.DASHBOARD_BUSINESS_HEADLINE')}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        hasIcon={true}
                                        icon={MagnifyingGlassIcon}
                                        onClick={() => goTo('/contacts#search')}
                                    >
                                        {t('NODATA.DASHBOARD_BUSINESS_BUTTON')}
                                    </Button>
                                </div>
                                <div className="tw-flex tw-flex-col tw-items-center tw-gap-5">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                        <Icon
                                            icon={ChatBubbleLeftRightIcon}
                                            className="tw-h-12 tw-w-12 tw-aspect-square tw-icon-primary"
                                        />
                                        <p className="tw-m-0 tw-text-center tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                            {t('NODATA.PUBLISH_COMMUNICATION_CHANNELS_HEADLINE')}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        hasIcon={true}
                                        icon={PlusIcon}
                                        onClick={() => goTo('/communicationchannels')}
                                    >
                                        {t('NODATA.PUBLISH_COMMUNICATION_CHANNELS_BUTTON')}
                                    </Button>
                                </div>
                            </div>
                        </DialogBody>
                    ) : (
                        <DialogBody padding="none" className="tw-flex tw-flex-col tw-min-h-0 tw-h-[80vh] tw-overflow-hidden tw-px-5 lg:tw-px-8 tw-pt-5 tw-pb-10 tw-gap-10">
                            <div className="tw-flex tw-flex-col tw-gap-5">
                                <StepProgress />
                                <Callout variant="info" canBeClosed={true}>
                                    {stepNotification}
                                </Callout>
                            </div>
                            <div className="tw-flex-1 tw-overflow-y-auto">
                                {currentStep === 1 && (
                                    <div className="tw-flex tw-flex-col tw-gap-10">
                                        <div className="tw-flex tw-flex-col tw-gap-1">
                                            <label htmlFor="recipient-search-input" className="tw-text-t-sm tw-m-0 tw-min-w-0 tw-text-t-secondary">
                                                {t('SEARCH_CONTACTS_LABEL')}
                                            </label>
                                            <Input
                                                id="recipient-search-input"
                                                icon={FunnelIcon}
                                                value={channelFilter}
                                                placeholder={t('SEARCH')}
                                                onChange={(event) => setChannelFilter(event.target.value)}
                                            />
                                        </div>
                                        <div className="tw-flex tw-flex-col tw-gap-4">

                                            <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-t-sm tw-m-0 tw-min-w-0 tw-text-t-secondary" htmlFor="selectAllRecipients">
                                                <Checkbox
                                                    id="selectAllRecipients"
                                                    isChecked={allRecipientsSelected}
                                                    onCheckedChange={(checked) => handleSelectAll(checked === true)}
                                                />
                                                <span>{t('SELECT_ALL')}</span>
                                            </label>
                                            {noMatches && <span>{t('NO_MATCHES')}</span>}
                                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-10 tw-gap-y-5">
                                                {filteredOrganizations.map((organizationEntry) => (
                                                    <OrganizationCard
                                                        key={`org-${organizationEntry.organizationId}`}
                                                        organization={organizationEntry}
                                                        onToggle={() => {
                                                            const next = setDestinationSelection(
                                                                organizations,
                                                                communicationChannels,
                                                                organizationEntry,
                                                                !organizationEntry.isSelected,
                                                            );
                                                            applySelectionChange(next.organizations, next.communicationChannels);
                                                        }}
                                                    />
                                                ))}
                                                {filteredChannels.map((channel) => (
                                                    <ChannelCard
                                                        key={`ch-${channel.id}-${channel.subDestinationKey ?? ''}`}
                                                        channel={channel}
                                                        attributes={getPublicationAttributes(channel.plan, plans, mode)}
                                                        onToggle={() => {
                                                            const next = setDestinationSelection(
                                                                organizations,
                                                                communicationChannels,
                                                                channel,
                                                                !channel.isSelected,
                                                            );
                                                            applySelectionChange(next.organizations, next.communicationChannels);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {currentStep === steps.length && (
                                    <div className="tw-flex tw-flex-col tw-gap-10">
                                        {queryRows.length > 0 && (
                                            <div data-testid="items-query-table" className="tw-flex tw-flex-col tw-gap-2">
                                                <p className="tw-text-t-sm lg:tw-text-t-base tw-text-h-primary tw-m-0">{t('ITEM_LIBRARY_PUBLISH.ITEMS_QUERY')}</p>
                                                <div className="tw-max-h-40 tw-overflow-y-auto">
                                                    <table className="tw-w-full tw-border-collapse tw-border-x-0 tw-text-t-sm tw-text-t-primary">
                                                        <tbody>
                                                            {queryRows.map((row) => (
                                                                <tr
                                                                    key={`${row.label}-${row.value}`}
                                                                    className="odd:tw-bg-fill-tertiary even:tw-bg-fill-primary tw-border-y tw-border-x-0 tw-border-solid tw-border-tertiary"
                                                                >
                                                                    <td className="tw-border-x-0 tw-text-t-secondary tw-w-5 tw-py-1 tw-px-2">{row.label}</td>
                                                                    <td className="tw-border-x-0 tw-text-t-primary tw-py-1 tw-px-2">{row.value}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                        <SchedulePlan planOptions={planOptions} onChange={setPlanOptions} />
                                    </div>
                                )}
                            </div>
                        </DialogBody>
                    )}
                    <DialogFooter className="sm:tw-justify-between">
                        <div>
                            {!isLoading && !isEmpty && currentStep > 1 && (
                                <Button
                                    variant="secondary"
                                    hasIcon={true}
                                    icon={ArrowLeftIcon}
                                    onClick={prevStep}
                                    data-testid="publish-previous"
                                >
                                    {t('PREVIOUS')}
                                </Button>
                            )}
                        </div>
                        <div className="tw-flex tw-gap-2">
                            {!isLoading && !isEmpty && currentStep < steps.length && (
                                <Button onClick={handleNext} data-testid="publish-next">
                                    {t('NEXT')}
                                </Button>
                            )}
                            {!isLoading && !isEmpty && currentStep === steps.length && (
                                <Button
                                    hasIcon={mode !== PUBLICATION_MODE.DEPUBLISH}
                                    icon={CloudArrowUpIcon}
                                    onClick={confirmPublication}
                                    disabled={isSubmitting}
                                    data-testid="publish-confirm"
                                >
                                    {lastStepLabel}
                                </Button>
                            )}
                            <DialogClose asChild={true}>
                                <Button variant="secondary" onClick={() => closeDialog()}>
                                    {t('CANCEL')}
                                </Button>
                            </DialogClose>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </FormProvider>
    );
};

const PublishToRecipientsDialog = (props: PublishToRecipientsDialogProps) => {
    const destinations = usePublicationDestinations(props.mode);
    const items = useChannelPublicationItems({
        channels: destinations.communicationChannels,
        plans: destinations.plans,
        mode: props.mode,
        job: props.publicationData?.job,
        enabled: !destinations.isLoading,
    });
    const publishMutation = usePublishItems();
    const jobMutation = useSavePublicationJob();
    const { t } = useTranslation();

    const steps = [
        t('ITEM_LIBRARY_PUBLISH.CHANNEL.LABEL'),
        t('ITEM_LIBRARY_PUBLISH.SCHEDULE.LABEL'),
    ];

    return (
        <FormResetProvider>
            <Stepper steps={steps}>
                <PublishToRecipientsDialogView
                    {...props}
                    organizations={destinations.organizations}
                    communicationChannels={destinations.communicationChannels}
                    plans={destinations.plans}
                    customPublicationSettings={destinations.customPublicationSettings}
                    user={destinations.user}
                    organization={destinations.organization}
                    isDestinationsLoading={destinations.isLoading}
                    itemsByChannel={items.itemsByChannel}
                    isItemsLoading={items.isLoading}
                    onPublish={publishMutation.mutate}
                    onSaveJob={jobMutation.mutate}
                    isSubmitting={publishMutation.isPending || jobMutation.isPending}
                />
            </Stepper>
        </FormResetProvider>
    );
};

PublishToRecipientsDialog.displayName = 'PublishToRecipientsDialog';
PublishToRecipientsDialogView.displayName = 'PublishToRecipientsDialogView';

export {
    PublishToRecipientsDialog,
    PublishToRecipientsDialogView,
};
export type {
    PublishToRecipientsDialogProps,
    PublishToRecipientsDialogViewProps,
};
